import { useState, useEffect } from 'react'
import type { SurveyRecord, InterviewRecord } from '@bandos/shared'
import { MindmapSurveyService } from '../../../../../features/survey/services/mindmapSurveyService'
import { MindmapInterviewService } from '../../../../../features/interview/services/mindmapInterviewService'

export interface ResearchSummaryItem {
  id: string
  type: 'survey' | 'interview'
  label: string
  parentLabel: string | null
  respondentCount: number
  createdAt: string
}

interface UseResearchSummaryReturn {
  items: ResearchSummaryItem[]
  count: number
  loading: boolean
}

function surveyToSummary(record: SurveyRecord): ResearchSummaryItem {
  return {
    id: record.id,
    type: 'survey',
    label: record.label,
    parentLabel: record.parentLabel,
    respondentCount: record.respondentCount,
    createdAt: record.createdAt,
  }
}

function interviewToSummary(record: InterviewRecord): ResearchSummaryItem {
  return {
    id: record.id,
    type: 'interview',
    label: record.label,
    parentLabel: record.parentLabel,
    respondentCount: record.respondentCount,
    createdAt: record.createdAt,
  }
}

export function useResearchSummary(mindmapId: string | undefined): UseResearchSummaryReturn {
  const [items, setItems] = useState<ResearchSummaryItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!mindmapId) {
      setItems([])
      setLoading(false)
      return undefined
    }
    let cancelled = false
    setLoading(true)

    void Promise.all([
      MindmapSurveyService.list(mindmapId),
      MindmapInterviewService.list(mindmapId),
    ])
      .then(([surveysRes, interviewsRes]) => {
        if (cancelled) return
        const surveys = surveysRes.data?.surveys ?? []
        const interviews = interviewsRes.data?.interviews ?? []
        const combined: ResearchSummaryItem[] = [
          ...surveys.map(surveyToSummary),
          ...interviews.map(interviewToSummary),
        ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        setItems(combined)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [mindmapId])

  return { items, count: items.length, loading }
}
